import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Save, RefreshCw, AlertTriangle, X, CheckCircle, Printer, Eye, Trash2, ChevronLeft, ChevronRight, MapPin, Home, Search, RefreshCcw, Edit, Info } from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import HighlightList from '../ui/HighlightList';
import { addLocalDays, formatLocalDate, parseLocalDateParts, toInputDate } from '../../utils/dateLocal';

/** Optional FK: null when not selected (do not send 0). */
function optionalId(value) {
  if (value === null || value === undefined || value === '') return null;
  const id = parseInt(value, 10);
  return Number.isFinite(id) && id > 0 ? id : null;
}

const CreateOrder = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [order, setOrder] = useState({
    Id: 0,
    VoucherNo: '',
    StoreRefNo: 0,
    ChargeFrom: 'CLIENT',
    PayType: 'CAD',
    Remarks: '',
    ClientBillNo: '',
    Quantity: 1,
    ProductPrice: 0,
    DeliveryCharge: 0,
    TAX: 0,
    CustomerAmount: 0,
    ClientAmount: 0,
    PickupDate: formatLocalDate(),
    DeliveryDate: formatLocalDate(),
    IsExchange: false,
    IsPickup: false,
    IsFragile: false,
    IsFree: false,
    IsAdvancePay: false,
    IsAdvancePrice: false,
    IsIssuedVoucher: false,
    TypeId: '',
    PickUpById: '',
    ClientId: '',
    ClientAddressId: '',
    CustomerId: '',
    CustomerAddressId: '',
    AgentId: '',
    TempServiceCharge: 0,
    ISCustomerEdit: false,
    delchargechanged: false,
  });

  const [client, setClient] = useState({
    Name: '',
    ClientNo: '',
    MobileNo1: '',
    IsZeroCharge: false,
  });

  const [clientAddress, setClientAddress] = useState({
    POBOX: '',
    Address: '',
    LocationUrl: '',
    LocationId: '',
    EmirateId: '',
    Location: { Name: '', AreaId: '' },
    Emirate: { Name: '' },
  });

  const [customer, setCustomer] = useState({
    Name: '',
    MobileNo1: '',
    MobileNo2: '',
  });

  const [customerAddress, setCustomerAddress] = useState({
    POBOX: '',
    Address: '',
    LocationUrl: '',
    LocationId: '',
    EmirateId: '',
    Location: { Name: '', AreaId: '', Area: { Name: '' } },
    Emirate: { Name: '' },
  });

  // Dropdown data
  const [types, setTypes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [agents, setAgents] = useState([]);
  const [companyProfile, setCompanyProfile] = useState({});
  
  // Autocomplete data
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  const [clientNoSearchTerm, setClientNoSearchTerm] = useState('');
  const [showClientNoDropdown, setShowClientNoDropdown] = useState(false);
  
  const [clientMobileSearchTerm, setClientMobileSearchTerm] = useState('');
  const [showClientMobileDropdown, setShowClientMobileDropdown] = useState(false);
  
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  const [customerMobileSearchTerm, setCustomerMobileSearchTerm] = useState('');
  const [showCustomerMobileDropdown, setShowCustomerMobileDropdown] = useState(false);
  
  const [clientLocationSearchTerm, setClientLocationSearchTerm] = useState('');
  const [clientLocationResults, setClientLocationResults] = useState([]);
  const [showClientLocationDropdown, setShowClientLocationDropdown] = useState(false);
  
  const [customerLocationSearchTerm, setCustomerLocationSearchTerm] = useState('');
  const [customerLocationResults, setCustomerLocationResults] = useState([]);
  const [showCustomerLocationDropdown, setShowCustomerLocationDropdown] = useState(false);

  const [pickupBySearchTerm, setPickupBySearchTerm] = useState('');
  const [pickupBySearchResults, setPickupBySearchResults] = useState([]);
  const [showPickupByDropdown, setShowPickupByDropdown] = useState(false);

  // Store all locations for client and customer
  const [allClientLocations, setAllClientLocations] = useState([]);
  const [allCustomerLocations, setAllCustomerLocations] = useState([]);

  // Refs to prevent search during selection
  const isSelectingClient = useRef(false);
  const isSelectingCustomer = useRef(false);

  // Address modals
  const [showClientAddressModal, setShowClientAddressModal] = useState(false);
  const [showCustomerAddressModal, setShowCustomerAddressModal] = useState(false);
  const [clientAddresses, setClientAddresses] = useState([]);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  
  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Payment info modal
  const [showPaymentInfoModal, setShowPaymentInfoModal] = useState(false);
  
  const [oldEmirateId, setOldEmirateId] = useState('');

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load dropdowns
      const [typesResp, driversResp, emiratesResp, agentsResp, profileResp] = await Promise.all([
        MainService.TypesInDropDown(),
        MainService.DriversInDropDown(),
        MainService.EmiratesInDropDown(),
        MainService.AgentsInDropDown(),
        MainService.GetCompanyProfile(),
      ]);

      setTypes(typesResp || []);
      setDrivers(driversResp || []);
      setEmirates(emiratesResp || []);
      setAgents(agentsResp || []);
      
      if (profileResp) {
        setCompanyProfile(profileResp);
        
        // Set defaults from company profile
        setOrder(prev => ({
          ...prev,
          ChargeFrom: profileResp.DefChargeFrom || 'CLIENT',
          PayType: profileResp.DefPayType || 'CAD',
          TypeId: profileResp.DefaultTypeId || '',
          DeliveryCharge: profileResp.DefDelCharge || 0,
          TempServiceCharge: profileResp.DefDelCharge || 0,
        }));

        // Set delivery date based on IsSameDay
        if (profileResp.IsSameDay === false) {
          setOrder(prev => ({
            ...prev,
            DeliveryDate: addLocalDays(1),
          }));
        }

        // Get next voucher number if auto-generate is enabled
        if (profileResp.IsAutoGenerateVoucherNo) {
          const voucherResp = await MainService.GetNextOrderNo();
          if (voucherResp) {
            setOrder(prev => ({ ...prev, VoucherNo: voucherResp }));
          }
        }
      }

      // Calculate totals after setting defaults
      setTimeout(() => calculateTotals(), 100);
      
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const profile = companyProfile;
    setOrder(prev => {
      let updatedOrder = { ...prev };

      // Handle delivery charge
      if (updatedOrder.Id > 0 && updatedOrder.delchargechanged === false && profile.IsVATFromDelivery) {
        // Keep existing delivery charge
      } else {
        updatedOrder.DeliveryCharge = updatedOrder.TempServiceCharge;
      }

      // Calculate TAX
      if (profile.DefTAX > 0) {
        let tdel = updatedOrder.DeliveryCharge + updatedOrder.TAX;

        if (updatedOrder.delchargechanged === true) {
          tdel = updatedOrder.DeliveryCharge;
        } else {
          tdel = updatedOrder.DeliveryCharge + updatedOrder.TAX;
        }

        if (profile.IsVATFromDelivery) {
          if (updatedOrder.Id > 0 && (updatedOrder.delchargechanged === false || updatedOrder.delchargechanged === undefined)) {
            updatedOrder.TAX = (tdel * profile.DefTAX) / (100 + profile.DefTAX);
          } else {
            updatedOrder.TAX = (updatedOrder.DeliveryCharge * profile.DefTAX) / (100 + profile.DefTAX);
          }
          updatedOrder.TAX = Math.round(updatedOrder.TAX * 100) / 100;

          if (updatedOrder.Id > 0 && (updatedOrder.delchargechanged === false || updatedOrder.delchargechanged === undefined)) {
            updatedOrder.DeliveryCharge = tdel - updatedOrder.TAX;
          } else {
            updatedOrder.DeliveryCharge = updatedOrder.DeliveryCharge - updatedOrder.TAX;
          }
        } else {
          updatedOrder.TAX = updatedOrder.DeliveryCharge * (profile.DefTAX / 100);
          updatedOrder.TAX = Math.round(updatedOrder.TAX * 100) / 100;
        }

        updatedOrder.delchargechanged = false;
      }

      // Set flags based on PayType
      updatedOrder.IsFree = false;
      updatedOrder.IsAdvancePay = false;
      updatedOrder.IsAdvancePrice = false;

      if (updatedOrder.PayType === 'FOC') {
        updatedOrder.IsFree = true;
        updatedOrder.DeliveryCharge = 0;
        updatedOrder.TAX = 0;
      }

      if (updatedOrder.PayType === 'CAO' || updatedOrder.PayType === 'TAO') {
        updatedOrder.IsAdvancePay = true;
      }

      if (updatedOrder.PayType === 'APS' || updatedOrder.PayType === 'APR') {
        updatedOrder.IsAdvancePrice = true;
      }

      // Calculate amounts based on PayType
      if (updatedOrder.PayType === 'CAD') {
        if (profile.IsVATFromCustomerOnly) {
          updatedOrder.CustomerAmount = updatedOrder.ProductPrice + updatedOrder.TAX;
          updatedOrder.ClientAmount = updatedOrder.ProductPrice - updatedOrder.DeliveryCharge;
        } else {
          updatedOrder.CustomerAmount = updatedOrder.ProductPrice;
          updatedOrder.ClientAmount = updatedOrder.ProductPrice - (updatedOrder.DeliveryCharge + updatedOrder.TAX);
        }
      } else if (updatedOrder.PayType === 'COD') {
        updatedOrder.CustomerAmount = updatedOrder.ProductPrice + updatedOrder.DeliveryCharge + updatedOrder.TAX;
        updatedOrder.ClientAmount = updatedOrder.ProductPrice;
      } else if (updatedOrder.PayType === 'CAO') {
        updatedOrder.CustomerAmount = updatedOrder.ProductPrice;
        updatedOrder.ClientAmount = updatedOrder.ProductPrice;
      } else if (updatedOrder.PayType === 'TAO') {
        updatedOrder.CustomerAmount = updatedOrder.ProductPrice;
        updatedOrder.ClientAmount = updatedOrder.ProductPrice;
      } else if (updatedOrder.PayType === 'FOC') {
        updatedOrder.CustomerAmount = updatedOrder.ProductPrice;
        updatedOrder.ClientAmount = updatedOrder.ProductPrice;
      } else if (updatedOrder.PayType === 'APS') {
        if (profile.IsVATFromCustomerOnly) {
          updatedOrder.CustomerAmount = updatedOrder.ProductPrice + updatedOrder.TAX;
          updatedOrder.ClientAmount = 0 - (updatedOrder.ProductPrice - updatedOrder.DeliveryCharge);
        } else {
          updatedOrder.CustomerAmount = updatedOrder.ProductPrice;
          updatedOrder.ClientAmount = 0 - (updatedOrder.ProductPrice - (updatedOrder.DeliveryCharge + updatedOrder.TAX));
        }
      } else if (updatedOrder.PayType === 'APR') {
        updatedOrder.CustomerAmount = updatedOrder.ProductPrice + updatedOrder.DeliveryCharge + updatedOrder.TAX;
        updatedOrder.ClientAmount = 0 - updatedOrder.ProductPrice;
      }

      updatedOrder.CustomerAmount = Math.round(updatedOrder.CustomerAmount * 100) / 100;
      updatedOrder.ClientAmount = Math.round(updatedOrder.ClientAmount * 100) / 100;

      return updatedOrder;
    });
  };

  // Client search (by name)
  useEffect(() => {
    if (isSelectingClient.current) return; // Skip search during selection
    
    if (clientSearchTerm && clientSearchTerm.length >= 2 && !order.ClientId) {
      const timer = setTimeout(async () => {
        if (isSelectingClient.current) return; // Double check
        
        try {
          const response = await MainService.ClientsInList({
            Keyword: clientSearchTerm,
            Skip: 0,
            Take: 25,
          });
          // Only show dropdown if still no client selected
          if (!order.ClientId && !isSelectingClient.current) {
            setClientSearchResults(response?.Data || []);
            setShowClientDropdown(true);
          }
        } catch (err) {
          console.error('Error searching clients:', err);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowClientDropdown(false);
      if (order.ClientId) {
        setClientSearchResults([]);
      }
    }
  }, [clientSearchTerm, order.ClientId]);

  // Client No search
  useEffect(() => {
    if (isSelectingClient.current) return; // Skip search during selection
    
    if (clientNoSearchTerm && clientNoSearchTerm.length >= 2 && !order.ClientId) {
      const timer = setTimeout(async () => {
        if (isSelectingClient.current) return; // Double check
        
        try {
          const response = await MainService.ClientsInList({
            Keyword: clientNoSearchTerm,
            Skip: 0,
            Take: 25,
          });
          // Only show dropdown if still no client selected
          if (!order.ClientId && !isSelectingClient.current) {
            setClientSearchResults(response?.Data || []);
            setShowClientNoDropdown(true);
          }
        } catch (err) {
          console.error('Error searching clients:', err);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowClientNoDropdown(false);
      if (order.ClientId) {
        setClientSearchResults([]);
      }
    }
  }, [clientNoSearchTerm, order.ClientId]);

  // Client Mobile search
  useEffect(() => {
    if (isSelectingClient.current) return; // Skip search during selection
    
    if (clientMobileSearchTerm && clientMobileSearchTerm.length >= 5 && !order.ClientId) {
      const timer = setTimeout(async () => {
        if (isSelectingClient.current) return; // Double check
        
        try {
          const response = await MainService.ClientsInList({
            Keyword: clientMobileSearchTerm,
            Skip: 0,
            Take: 25,
          });
          // Only show dropdown if still no client selected
          if (!order.ClientId && !isSelectingClient.current) {
            setClientSearchResults(response?.Data || []);
            setShowClientMobileDropdown(true);
          }
        } catch (err) {
          console.error('Error searching clients:', err);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowClientMobileDropdown(false);
      if (order.ClientId) {
        setClientSearchResults([]);
      }
    }
  }, [clientMobileSearchTerm, order.ClientId]);

  // Customer search (by name)
  useEffect(() => {
    if (isSelectingCustomer.current) return; // Skip search during selection
    
    if (customerSearchTerm && customerSearchTerm.length >= 2 && (!order.CustomerId || order.ISCustomerEdit)) {
      const timer = setTimeout(async () => {
        if (isSelectingCustomer.current) return; // Double check
        
        try {
          const response = await MainService.CustomersInList({
            Keyword: customerSearchTerm,
            Skip: 0,
            Take: 10,
          });
          // Only show dropdown if still no customer selected or in edit mode
          if ((!order.CustomerId || order.ISCustomerEdit) && !isSelectingCustomer.current) {
            setCustomerSearchResults(response?.Data || []);
            setShowCustomerDropdown(true);
          }
        } catch (err) {
          console.error('Error searching customers:', err);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowCustomerDropdown(false);
      if (order.CustomerId && !order.ISCustomerEdit) {
        setCustomerSearchResults([]);
      }
    }
  }, [customerSearchTerm, order.CustomerId, order.ISCustomerEdit]);

  // Customer Mobile search
  useEffect(() => {
    if (isSelectingCustomer.current) return; // Skip search during selection
    
    if (customerMobileSearchTerm && customerMobileSearchTerm.length >= 5 && (!order.CustomerId || order.ISCustomerEdit)) {
      const timer = setTimeout(async () => {
        if (isSelectingCustomer.current) return; // Double check
        
        try {
          const response = await MainService.CustomersInList({
            Keyword: customerMobileSearchTerm,
            Skip: 0,
            Take: 10,
          });
          // Only show dropdown if still no customer selected or in edit mode
          if ((!order.CustomerId || order.ISCustomerEdit) && !isSelectingCustomer.current) {
            setCustomerSearchResults(response?.Data || []);
            setShowCustomerMobileDropdown(true);
          }
        } catch (err) {
          console.error('Error searching customers:', err);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowCustomerMobileDropdown(false);
      if (order.CustomerId && !order.ISCustomerEdit) {
        setCustomerSearchResults([]);
      }
    }
  }, [customerMobileSearchTerm, order.CustomerId, order.ISCustomerEdit]);

  // Load all client locations when emirate is selected
  useEffect(() => {
    const loadClientLocations = async () => {
      if (clientAddress.EmirateId && !order.ClientAddressId) {
        try {
          const response = await MainService.LocationByEmirateId(clientAddress.EmirateId);
          setAllClientLocations(response || []);
          setClientLocationResults(response || []);
        } catch (err) {
          console.error('Error loading client locations:', err);
        }
      } else {
        setAllClientLocations([]);
        setClientLocationResults([]);
      }
    };
    loadClientLocations();
  }, [clientAddress.EmirateId, order.ClientAddressId]);

  // Client Location search/filter
  useEffect(() => {
    if (!order.ClientAddressId && clientAddress.EmirateId) {
      if (clientLocationSearchTerm && clientLocationSearchTerm.length >= 2) {
        const timer = setTimeout(() => {
          const filtered = allClientLocations.filter(loc => 
            loc.Name.toLowerCase().includes(clientLocationSearchTerm.toLowerCase())
          );
          setClientLocationResults(filtered);
        }, 300);
        return () => clearTimeout(timer);
      } else {
        // Show all locations when no search term
        setClientLocationResults(allClientLocations);
      }
    }
  }, [clientLocationSearchTerm, allClientLocations, order.ClientAddressId, clientAddress.EmirateId]);

  // Load all customer locations when emirate is selected
  useEffect(() => {
    const loadCustomerLocations = async () => {
      if (customerAddress.EmirateId && (!order.CustomerAddressId || order.ISCustomerEdit)) {
        try {
          const response = await MainService.LocationByEmirateId(customerAddress.EmirateId);
          setAllCustomerLocations(response || []);
          setCustomerLocationResults(response || []);
        } catch (err) {
          console.error('Error loading customer locations:', err);
        }
      } else {
        setAllCustomerLocations([]);
        setCustomerLocationResults([]);
      }
    };
    loadCustomerLocations();
  }, [customerAddress.EmirateId, order.CustomerAddressId, order.ISCustomerEdit]);

  // Customer Location search/filter
  useEffect(() => {
    if ((!order.CustomerAddressId || order.ISCustomerEdit) && customerAddress.EmirateId) {
      if (customerLocationSearchTerm && customerLocationSearchTerm.length >= 2) {
        const timer = setTimeout(() => {
          const filtered = allCustomerLocations.filter(loc => 
            loc.Name.toLowerCase().includes(customerLocationSearchTerm.toLowerCase())
          );
          setCustomerLocationResults(filtered);
        }, 300);
        return () => clearTimeout(timer);
      } else {
        // Show all locations when no search term
        setCustomerLocationResults(allCustomerLocations);
      }
    }
  }, [customerLocationSearchTerm, allCustomerLocations, order.CustomerAddressId, order.ISCustomerEdit, customerAddress.EmirateId]);

  // Pick Up By search
  useEffect(() => {
    if (!order.PickUpById) {
      if (pickupBySearchTerm && pickupBySearchTerm.length >= 2) {
        const timer = setTimeout(() => {
          const filtered = drivers.filter(d => 
            d.Name.toLowerCase().includes(pickupBySearchTerm.toLowerCase())
          );
          setPickupBySearchResults(filtered);
        }, 300);
        return () => clearTimeout(timer);
      } else {
        // Show all drivers when no search term
        setPickupBySearchResults(drivers);
      }
    }
  }, [pickupBySearchTerm, drivers, order.PickUpById]);

  // Select client from autocomplete
  const selectClient = (selectedClient) => {
    console.log('Selected Client:', selectedClient);
    console.log('Client ID:', selectedClient.Id);
    
    // Set flag to prevent search during selection
    isSelectingClient.current = true;
    
    // Close dropdowns and clear results
    setShowClientDropdown(false);
    setShowClientNoDropdown(false);
    setShowClientMobileDropdown(false);
    setClientSearchResults([]);
    
    // Update client data
    setClient({
      Name: selectedClient.Name || '',
      ClientNo: selectedClient.ClientNo || '',
      MobileNo1: selectedClient.MobileNo1 || '',
      IsZeroCharge: selectedClient.IsZeroCharge || false,
    });
    
    // Set search terms immediately (fields are disabled so no issue)
    setClientSearchTerm(selectedClient.Name || '');
    setClientNoSearchTerm(selectedClient.ClientNo || '');
    setClientMobileSearchTerm(selectedClient.MobileNo1 || '');

    // Update order with ClientId and ClientAddressId
    const orderUpdates = { 
      ClientId: selectedClient.Id || ''
    };
    
    console.log('Order Updates:', orderUpdates);
    
    if (selectedClient.ClientAddressId && selectedClient.ClientAddressId > 0) {
      orderUpdates.ClientAddressId = selectedClient.ClientAddressId;
      
      setClientAddress({
        POBOX: selectedClient.POBOX || '',
        Address: selectedClient.Address || '',
        LocationUrl: selectedClient.LocationUrl || '',
        LocationId: selectedClient.LocationId || '',
        EmirateId: selectedClient.EmirateId || '',
        Location: { 
          Name: selectedClient.LocationName || '', 
          AreaId: '',
          Area: { Name: selectedClient.AreaName || '' }
        },
        Emirate: { Name: selectedClient.EmirateName || '' },
      });
      setClientLocationSearchTerm(selectedClient.LocationName || '');
    }
    
    // Update order state once with all values
    setOrder(prev => {
      const updated = { ...prev, ...orderUpdates };
      console.log('Updated Order State:', updated);
      return updated;
    });
    
    // Reset flag after longer delay to ensure all state updates complete
    setTimeout(() => {
      isSelectingClient.current = false;
    }, 500);
    
    checkDeliveryCharges();
  };

  // Select customer from autocomplete
  const selectCustomer = (selectedCustomer) => {
    console.log('Selected Customer:', selectedCustomer);
    console.log('Customer ID:', selectedCustomer.Id);
    
    // Set flag to prevent search during selection
    isSelectingCustomer.current = true;
    
    // Close dropdowns and clear results
    setShowCustomerDropdown(false);
    setShowCustomerMobileDropdown(false);
    setCustomerSearchResults([]);
    
    // Update customer data
    setCustomer({
      Name: selectedCustomer.Name || '',
      MobileNo1: selectedCustomer.MobileNo1 || '',
      MobileNo2: selectedCustomer.MobileNo2 || '',
    });
    
    // Set search terms immediately (fields are disabled so no issue)
    setCustomerSearchTerm(selectedCustomer.Name || '');
    setCustomerMobileSearchTerm(selectedCustomer.MobileNo1 || '');

    // Update order with CustomerId and CustomerAddressId
    const orderUpdates = { 
      CustomerId: selectedCustomer.Id || ''
    };
    
    console.log('Order Updates:', orderUpdates);

    if (selectedCustomer.CustomerAddressId && selectedCustomer.CustomerAddressId > 0) {
      orderUpdates.CustomerAddressId = selectedCustomer.CustomerAddressId;
      
      setCustomerAddress({
        POBOX: selectedCustomer.POBOX || '',
        Address: selectedCustomer.Address || '',
        LocationUrl: selectedCustomer.LocationUrl || '',
        LocationId: selectedCustomer.LocationId || '',
        EmirateId: selectedCustomer.EmirateId || '',
        Location: {
          Name: selectedCustomer.LocationName || '',
          AreaId: '',
          Area: { Name: selectedCustomer.AreaName || '' },
        },
        Emirate: { Name: selectedCustomer.EmirateName || '' },
      });
      setCustomerLocationSearchTerm(selectedCustomer.LocationName || '');
      if (selectedCustomer.LocationId) {
        getStoreRefNo(selectedCustomer.LocationId);
      }
    }
    
    // Update order state once with all values
    setOrder(prev => {
      const updated = { ...prev, ...orderUpdates };
      console.log('Updated Order State:', updated);
      return updated;
    });
    
    // Reset flag after longer delay to ensure all state updates complete
    setTimeout(() => {
      isSelectingCustomer.current = false;
    }, 500);
    
    checkDeliveryCharges();
  };

  // Select client location
  const selectClientLocation = (location) => {
    setClientAddress(prev => ({
      ...prev,
      LocationId: location.Id,
      Location: { 
        Name: location.Name, 
        AreaId: location.AreaId || '',
        Area: { Name: location.AreaName || '' }
      },
    }));
    setClientLocationSearchTerm(location.Name);
    setShowClientLocationDropdown(false);
  };

  // Select customer location
  const selectCustomerLocation = (location) => {
    setCustomerAddress(prev => ({
      ...prev,
      LocationId: location.Id,
      Location: {
        Name: location.Name,
        AreaId: location.AreaId || '',
        Area: { Name: location.AreaName || location.Area?.Name || '' },
      },
    }));
    setCustomerLocationSearchTerm(location.Name);
    setShowCustomerLocationDropdown(false);
    
    checkDeliveryCharges();
    if (location.Id) {
      getStoreRefNo(location.Id);
    }
  };

  // Select pickup driver
  const selectPickupDriver = (driver) => {
    setOrder(prev => ({ ...prev, PickUpById: driver.Id }));
    setPickupBySearchTerm(driver.Name);
    setShowPickupByDropdown(false);
  };

  // Check delivery charges
  const checkDeliveryCharges = async () => {
    try {
      setLoading(true);
      const response = await MainService.GetDeliveryCharge({
        ChargeFrom: order.ChargeFrom,
        LocationId: customerAddress.LocationId,
        ClientId: order.ClientId,
      });

      if (response > 0) {
        setOrder(prev => ({
          ...prev,
          DeliveryCharge: response,
          TempServiceCharge: response,
          delchargechanged: true,
        }));
      } else {
        if (client.IsZeroCharge === true) {
          setOrder(prev => ({
            ...prev,
            DeliveryCharge: 0,
            TempServiceCharge: 0,
            delchargechanged: true,
          }));
        } else {
          setOrder(prev => ({
            ...prev,
            DeliveryCharge: companyProfile.DefDelCharge || 0,
            TempServiceCharge: companyProfile.DefDelCharge || 0,
            delchargechanged: true,
          }));
        }
      }

      setTimeout(() => calculateTotals(), 100);
    } catch (err) {
      console.error('Error checking delivery charges:', err);
      setError('Failed to check delivery charges');
    } finally {
      setLoading(false);
    }
  };

  // Get store reference number
  const getStoreRefNo = async (locationId) => {
    if (companyProfile.IsBlockStoreRefLoad === false) {
      try {
        setLoading(true);
        const response = await MainService.GetStoreRefNo(locationId);
        if (response > 0) {
          setOrder(prev => ({ ...prev, StoreRefNo: response }));
        } else {
          setError('Not Able To Display Reference Number');
        }
      } catch (err) {
        console.error('Error getting store ref no:', err);
        setError('Network Error');
      } finally {
        setLoading(false);
      }
    }
  };

  // Check voucher
  const checkVoucher = async () => {
    if (!order.VoucherNo) return;

    try {
      setLoading(true);
      const response = await MainService.OrderByVoucherNo(order.VoucherNo);
      
      if (response) {
        setError('Voucher Exist');
        // Load existing order
        loadOrder(response);
      } else {
        // Check if voucher is assigned to a client
        const assignedResp = await MainService.CheckVoucherAssigned(order.VoucherNo);
        if (assignedResp) {
          setOrder(prev => ({ ...prev, IsIssuedVoucher: true, ClientId: assignedResp.Id }));
          setClient({
            Name: assignedResp.Name,
            ClientNo: assignedResp.ClientNo,
            MobileNo1: assignedResp.MobileNo1,
            IsZeroCharge: assignedResp.IsZeroCharge || false,
          });
          setClientSearchTerm(assignedResp.Name);
          setClientNoSearchTerm(assignedResp.ClientNo);
          setClientMobileSearchTerm(assignedResp.MobileNo1);

          if (assignedResp.ClientAddressId > 0) {
            setOrder(prev => ({ ...prev, ClientAddressId: assignedResp.ClientAddressId }));
            setClientAddress({
              POBOX: assignedResp.POBOX || '',
              Address: assignedResp.Address || '',
              LocationUrl: assignedResp.LocationUrl || '',
              LocationId: assignedResp.LocationId || '',
              EmirateId: assignedResp.EmirateId || '',
              Location: { 
                Name: assignedResp.LocationName || '', 
                AreaId: '',
                Area: { Name: assignedResp.AreaName || '' }
              },
              Emirate: { Name: assignedResp.EmirateName || '' },
            });
            setClientLocationSearchTerm(assignedResp.LocationName || '');
          }
          
          checkDeliveryCharges();
        }
      }
    } catch (err) {
      console.error('Error checking voucher:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Load order
  const loadOrder = (orderData) => {
    // Set order data
    setOrder({
      ...orderData,
      PickUpById: orderData.PickUpById ?? '',
      AgentId: orderData.AgentId ?? '',
      PickupDate: orderData.PickupDate ? toInputDate(orderData.PickupDate) : formatLocalDate(),
      DeliveryDate: orderData.DeliveryDate ? toInputDate(orderData.DeliveryDate) : formatLocalDate(),
    });

    // Set client data
    if (orderData.Client) {
      setClient(orderData.Client);
      setClientSearchTerm(orderData.Client.Name || '');
      setClientNoSearchTerm(orderData.Client.ClientNo || '');
      setClientMobileSearchTerm(orderData.Client.MobileNo1 || '');
    }

    // Set client address
    if (orderData.ClientAddress) {
      setClientAddress(orderData.ClientAddress);
      setClientLocationSearchTerm(orderData.ClientAddress.Location?.Name || '');
    }

    // Set customer data
    if (orderData.Customer) {
      setCustomer(orderData.Customer);
      setCustomerSearchTerm(orderData.Customer.Name || '');
      setCustomerMobileSearchTerm(orderData.Customer.MobileNo1 || '');
    }

    // Set customer address
    if (orderData.CustomerAddress) {
      setCustomerAddress(orderData.CustomerAddress);
      setCustomerLocationSearchTerm(orderData.CustomerAddress.Location?.Name || '');
    }

    // Set pickup driver
    if (orderData.PickUpById) {
      const driver = drivers.find(d => d.Id === orderData.PickUpById);
      if (driver) {
        setPickupBySearchTerm(driver.Name);
      }
    }
  };

  // Save order
  const saveOrder = async (printAfter = false) => {
    // Validation
    const statusErr = order.Id > 0 && order.Status && !['PKS', 'PKD', 'PKR', 'INS', 'RTN', 'INT'].includes(order.Status.Keyword);

    if (order.Id > 0 && statusErr) {
      setError('Cannot Update Order Because Its In Processing Stage');
      return;
    }
    if (!order.VoucherNo) {
      setError('Enter Voucher Number');
      return;
    }
    if (order.StoreRefNo === 0) {
      setError('Enter Store Ref Number');
      return;
    }
    if (companyProfile.IsPickupByComp && !order.PickUpById && !order.IsPickup) {
      setError('Select Pickup Driver');
      return;
    }
    // Client validation - check search terms for new clients or client object for selected
    if (!client.Name && !clientSearchTerm) {
      setError('Enter Client Name');
      return;
    }
    if (!client.MobileNo1 && !clientMobileSearchTerm) {
      setError('Enter Client Mobile No');
      return;
    }
    if (!clientAddress.EmirateId) {
      setError('Select Client Emirate');
      return;
    }
    if (!clientAddress.LocationId) {
      setError('Select Client Location');
      return;
    }
    // Customer validation - check search terms for new customers or customer object for selected
    if (!customer.Name && !customerSearchTerm) {
      setError('Enter Customer Name');
      return;
    }
    if (!customer.MobileNo1 && !customerMobileSearchTerm) {
      setError('Enter Customer Mobile No');
      return;
    }
    if (!customerAddress.EmirateId) {
      setError('Select Customer Emirate');
      return;
    }
    if (!customerAddress.LocationId) {
      setError('Select Customer Location');
      return;
    }
    if (!order.IsFree && order.DeliveryCharge === 0 && client.IsZeroCharge !== true) {
      setError('Enter Delivery Charge Or Make Order As Free');
      return;
    }

    try {
      setLoading(true);

      // Parse dates as local calendar parts (avoid UTC shift from new Date('YYYY-MM-DD'))
      const pickupParts = parseLocalDateParts(order.PickupDate);
      const deliveryParts = parseLocalDateParts(order.DeliveryDate);

      // Prepare client data - use search terms if client not selected (new client)
      const clientData = {
        ...client,
        Name: client.Name || clientSearchTerm,
        ClientNo: client.ClientNo || clientNoSearchTerm,
        MobileNo1: client.MobileNo1 || clientMobileSearchTerm,
        MobileNo2: client.MobileNo2 || '',
      };

      // Prepare customer data - use search terms if customer not selected (new customer)
      const customerData = {
        ...customer,
        Name: customer.Name || customerSearchTerm,
        MobileNo1: customer.MobileNo1 || customerMobileSearchTerm,
        MobileNo2: customer.MobileNo2 || '', // MobileNo2 is optional
      };

      // Prepare client address with proper numeric IDs
      const clientAddressData = {
        ...clientAddress,
        EmirateId: parseInt(clientAddress.EmirateId) || 0,
        LocationId: parseInt(clientAddress.LocationId) || 0,
      };

      // Prepare customer address with proper numeric IDs
      const customerAddressData = {
        ...customerAddress,
        EmirateId: parseInt(customerAddress.EmirateId) || 0,
        LocationId: parseInt(customerAddress.LocationId) || 0,
      };

      const payload = {
        ...order,
        // Ensure IDs are numbers, not empty strings
        ClientId: parseInt(order.ClientId) || 0,
        CustomerId: parseInt(order.CustomerId) || 0,
        ClientAddressId: parseInt(order.ClientAddressId) || 0,
        CustomerAddressId: parseInt(order.CustomerAddressId) || 0,
        PickUpById: optionalId(order.PickUpById),
        AgentId: optionalId(order.AgentId),
        TypeId: parseInt(order.TypeId) || 0,
        PMonth: pickupParts.month,
        PDay: pickupParts.day,
        PYear: pickupParts.year,
        DMonth: deliveryParts.month,
        DDay: deliveryParts.day,
        DYear: deliveryParts.year,
        Client: clientData,
        ClientAddress: clientAddressData,
        Customer: customerData,
        CustomerAddress: customerAddressData,
      };

      console.log('=== SAVE ORDER PAYLOAD ===');
      console.log('Order.ClientId:', payload.ClientId, '(0 = new client)');
      console.log('Order.CustomerId:', payload.CustomerId, '(0 = new customer)');
      console.log('Order.ClientAddressId:', payload.ClientAddressId, '(0 = new address)');
      console.log('Order.CustomerAddressId:', payload.CustomerAddressId, '(0 = new address)');
      console.log('Client Data:', payload.Client);
      console.log('Customer Data:', payload.Customer);
      console.log('Client Address:', payload.ClientAddress);
      console.log('Customer Address:', payload.CustomerAddress);
      console.log('Full Payload:', payload);
      console.log('========================');

      const response = await MainService.SaveOrder(payload);

      if (response?.Status === 'Success') {
        setSuccess('Success');
        
        if (printAfter && response.OrderId) {
          printOrder(response.OrderId);
        }

        // Save old emirate ID and refresh
        setOldEmirateId(customerAddress.EmirateId);
        setTimeout(() => {
          initForm();
        }, 1000);
      } else if (response?.Status === 'Failed') {
        setError('Failed');
      } else {
        setError(response?.Status || 'Unknown error');
      }
    } catch (err) {
      console.error('Error saving order:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Print order
  const printOrder = async (orderId) => {
    try {
      setLoading(true);
      const response = await MainService.PrintOrder(orderId || order.Id);
      
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank');
      } else {
        setError('Print Error');
      }
    } catch (err) {
      console.error('Error printing order:', err);
      setError('Print Error');
    } finally {
      setLoading(false);
    }
  };

  // Delete order
  const deleteOrder = () => {
    if (order.Id > 0 && order.Status && (order.Status.Keyword === 'INS' || order.Status.Keyword === 'PKS')) {
      setShowDeleteModal(true);
    } else {
      setError('Cannot Delete Order Because Its In Processing Stage');
    }
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await MainService.DeleteOrder(order.Id);
      
      if (response === 'Success') {
        setSuccess('Successfully Deleted');
        setShowDeleteModal(false);
        initForm();
      } else {
        setError(response);
      }
    } catch (err) {
      console.error('Error deleting order:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Get back/next order
  const getBackOrder = async (type) => {
    try {
      setLoading(true);
      const orderId = order.Id > 0 ? order.Id : 0;
      const response = await MainService.GetBackOrder(orderId, type);
      
      if (response > 0) {
        const orderResp = await MainService.OrderById(response);
        if (orderResp) {
          loadOrder(orderResp);
        }
      } else {
        initForm();
      }
    } catch (err) {
      console.error('Error getting back order:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Initialize form
  const initForm = () => {
    setOrder({
      Id: 0,
      VoucherNo: '',
      StoreRefNo: 0,
      ChargeFrom: companyProfile.DefChargeFrom || 'CLIENT',
      PayType: companyProfile.DefPayType || 'CAD',
      Remarks: '',
      ClientBillNo: '',
      Quantity: 1,
      ProductPrice: 0,
      DeliveryCharge: companyProfile.DefDelCharge || 0,
      TAX: 0,
      CustomerAmount: 0,
      ClientAmount: 0,
      PickupDate: formatLocalDate(),
      DeliveryDate: formatLocalDate(),
      IsExchange: false,
      IsPickup: false,
      IsFragile: false,
      IsFree: false,
      IsAdvancePay: false,
      IsAdvancePrice: false,
      IsIssuedVoucher: false,
      TypeId: companyProfile.DefaultTypeId || '',
      PickUpById: '',
      ClientId: '',
      ClientAddressId: '',
      CustomerId: '',
      CustomerAddressId: '',
      AgentId: '',
      TempServiceCharge: companyProfile.DefDelCharge || 0,
      ISCustomerEdit: false,
      delchargechanged: false,
    });

    setClient({ Name: '', ClientNo: '', MobileNo1: '', IsZeroCharge: false });
    setClientAddress({
      POBOX: '',
      Address: '',
      LocationUrl: '',
      LocationId: '',
      EmirateId: oldEmirateId && companyProfile.IsKeepEmirate ? oldEmirateId : '',
      Location: { Name: '', AreaId: '' },
      Emirate: { Name: '' },
    });
    setCustomer({ Name: '', MobileNo1: '', MobileNo2: '' });
    setCustomerAddress({
      POBOX: '',
      Address: '',
      LocationUrl: '',
      LocationId: '',
      EmirateId: oldEmirateId && companyProfile.IsKeepEmirate ? oldEmirateId : '',
      Location: { Name: '', AreaId: '', Area: { Name: '' } },
      Emirate: { Name: '' },
    });

    setClientSearchTerm('');
    setClientNoSearchTerm('');
    setClientMobileSearchTerm('');
    setCustomerSearchTerm('');
    setCustomerMobileSearchTerm('');
    setClientLocationSearchTerm('');
    setCustomerLocationSearchTerm('');
    setPickupBySearchTerm('');

    // Get next voucher if needed
    if (companyProfile.IsAutoGenerateVoucherNo) {
      MainService.GetNextOrderNo().then(voucherResp => {
        if (voucherResp) {
          setOrder(prev => ({ ...prev, VoucherNo: voucherResp }));
        }
      });
    }

    // Set delivery date based on IsSameDay
    if (companyProfile.IsSameDay === false) {
      setOrder(prev => ({
        ...prev,
        DeliveryDate: addLocalDays(1),
      }));
    }

    calculateTotals();
  };

  // Refresh functions
  const refreshClient = () => {
    setOrder(prev => ({
      ...prev,
      IsIssuedVoucher: false,
      ClientId: '',
      ClientAddressId: '',
    }));
    setClient({ Name: '', ClientNo: '', MobileNo1: '', IsZeroCharge: false });
    setClientAddress({
      POBOX: '',
      Address: '',
      LocationUrl: '',
      LocationId: '',
      EmirateId: '',
      Location: { Name: '', AreaId: '' },
      Emirate: { Name: '' },
    });
    setClientSearchTerm('');
    setClientNoSearchTerm('');
    setClientMobileSearchTerm('');
    setClientLocationSearchTerm('');
  };

  const refreshClientAddress = () => {
    setOrder(prev => ({ ...prev, ClientAddressId: '' }));
    setClientAddress({
      POBOX: '',
      Address: '',
      LocationUrl: '',
      LocationId: '',
      EmirateId: '',
      Location: { Name: '', AreaId: '' },
      Emirate: { Name: '' },
    });
    setClientLocationSearchTerm('');
  };

  const refreshCustomer = () => {
    setOrder(prev => ({
      ...prev,
      CustomerId: '',
      CustomerAddressId: '',
    }));
    setCustomer({ Name: '', MobileNo1: '', MobileNo2: '' });
    setCustomerAddress({
      POBOX: '',
      Address: '',
      LocationUrl: '',
      LocationId: '',
      EmirateId: '',
      Location: { Name: '', AreaId: '', Area: { Name: '' } },
      Emirate: { Name: '' },
    });
    setCustomerSearchTerm('');
    setCustomerMobileSearchTerm('');
    setCustomerLocationSearchTerm('');
  };

  const refreshCustomerAddress = () => {
    setOrder(prev => ({ ...prev, CustomerAddressId: '' }));
    setCustomerAddress({
      POBOX: '',
      Address: '',
      LocationUrl: '',
      LocationId: '',
      EmirateId: '',
      Location: { Name: '', AreaId: '', Area: { Name: '' } },
      Emirate: { Name: '' },
    });
    setCustomerLocationSearchTerm('');
  };

  // Search addresses
  const searchClientAddress = async () => {
    if (order.ClientId) {
      try {
        setLoading(true);
        const response = await MainService.GetClientAddress(order.ClientId);
        if (response) {
          setClientAddresses(response);
          setShowClientAddressModal(true);
        }
      } catch (err) {
        console.error('Error searching client addresses:', err);
        setError('Network Error');
      } finally {
        setLoading(false);
      }
    }
  };

  const searchCustomerAddress = async () => {
    if (order.CustomerId) {
      try {
        setLoading(true);
        const response = await MainService.GetCustomerAddress(order.CustomerId);
        if (response) {
          setCustomerAddresses(response);
          setShowCustomerAddressModal(true);
        }
      } catch (err) {
        console.error('Error searching customer addresses:', err);
        setError('Network Error');
      } finally {
        setLoading(false);
      }
    }
  };

  // Select addresses from modal
  const selectClientAddressFromModal = (addr) => {
    setOrder(prev => ({ ...prev, ClientAddressId: addr.Id }));
    setClientAddress({
      POBOX: addr.POBOX || '',
      Address: addr.Address || '',
      LocationUrl: addr.LocationUrl || '',
      LocationId: addr.LocationId || '',
      EmirateId: addr.EmirateId || '',
      Location: { Name: addr.Location?.Name || '', AreaId: addr.Location?.AreaId || '' },
      Emirate: { Name: addr.Emirate?.Name || '' },
    });
    setClientLocationSearchTerm(addr.Location?.Name || '');
    setShowClientAddressModal(false);
  };

  const selectCustomerAddressFromModal = (addr) => {
    setOrder(prev => ({ ...prev, CustomerAddressId: addr.Id }));
    setCustomerAddress({
      POBOX: addr.POBOX || '',
      Address: addr.Address || '',
      LocationUrl: addr.LocationUrl || '',
      LocationId: addr.LocationId || '',
      EmirateId: addr.EmirateId || '',
      Location: {
        Name: addr.Location?.Name || '',
        AreaId: addr.Location?.AreaId || '',
        Area: { Name: addr.Location?.AreaName || '' },
      },
      Emirate: { Name: addr.Emirate?.Name || '' },
    });
    setCustomerLocationSearchTerm(addr.Location?.Name || '');
    setShowCustomerAddressModal(false);
  };

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const clientDropdownOpen = showClientDropdown && clientSearchResults.length > 0 && !order.ClientId;
  const clientNoDropdownOpen = showClientNoDropdown && clientSearchResults.length > 0 && !order.ClientId;
  const clientMobileDropdownOpen = showClientMobileDropdown && clientSearchResults.length > 0 && !order.ClientId;
  const customerDropdownOpen = showCustomerDropdown && customerSearchResults.length > 0 && (!order.CustomerId || order.ISCustomerEdit);
  const customerMobileDropdownOpen = showCustomerMobileDropdown && customerSearchResults.length > 0 && (!order.CustomerId || order.ISCustomerEdit);
  const clientLocationOpen = showClientLocationDropdown && clientLocationResults.length > 0 && !order.ClientAddressId;
  const customerLocationOpen = showCustomerLocationDropdown && customerLocationResults.length > 0 && (!order.CustomerAddressId || order.ISCustomerEdit);
  const pickupDropdownOpen = showPickupByDropdown && pickupBySearchResults.length > 0 && !order.PickUpById;

  const clientNameNav = useListKeyboardNav({
    isOpen: clientDropdownOpen,
    itemCount: clientSearchResults.length,
    onSelectIndex: (index) => clientSearchResults[index] && selectClient(clientSearchResults[index]),
    onClose: () => setShowClientDropdown(false),
    resetKey: clientSearchTerm,
  });
  const clientNoNav = useListKeyboardNav({
    isOpen: clientNoDropdownOpen,
    itemCount: clientSearchResults.length,
    onSelectIndex: (index) => clientSearchResults[index] && selectClient(clientSearchResults[index]),
    onClose: () => setShowClientNoDropdown(false),
    resetKey: clientNoSearchTerm,
  });
  const clientMobileNav = useListKeyboardNav({
    isOpen: clientMobileDropdownOpen,
    itemCount: clientSearchResults.length,
    onSelectIndex: (index) => clientSearchResults[index] && selectClient(clientSearchResults[index]),
    onClose: () => setShowClientMobileDropdown(false),
    resetKey: clientMobileSearchTerm,
  });
  const customerNameNav = useListKeyboardNav({
    isOpen: customerDropdownOpen,
    itemCount: customerSearchResults.length,
    onSelectIndex: (index) => customerSearchResults[index] && selectCustomer(customerSearchResults[index]),
    onClose: () => setShowCustomerDropdown(false),
    resetKey: customerSearchTerm,
  });
  const customerMobileNav = useListKeyboardNav({
    isOpen: customerMobileDropdownOpen,
    itemCount: customerSearchResults.length,
    onSelectIndex: (index) => customerSearchResults[index] && selectCustomer(customerSearchResults[index]),
    onClose: () => setShowCustomerMobileDropdown(false),
    resetKey: customerMobileSearchTerm,
  });
  const clientLocationNav = useListKeyboardNav({
    isOpen: clientLocationOpen,
    itemCount: clientLocationResults.length,
    onSelectIndex: (index) => clientLocationResults[index] && selectClientLocation(clientLocationResults[index]),
    onClose: () => setShowClientLocationDropdown(false),
    resetKey: clientLocationSearchTerm,
  });
  const customerLocationNav = useListKeyboardNav({
    isOpen: customerLocationOpen,
    itemCount: customerLocationResults.length,
    onSelectIndex: (index) => customerLocationResults[index] && selectCustomerLocation(customerLocationResults[index]),
    onClose: () => setShowCustomerLocationDropdown(false),
    resetKey: customerLocationSearchTerm,
  });
  const pickupNav = useListKeyboardNav({
    isOpen: pickupDropdownOpen,
    itemCount: pickupBySearchResults.length,
    onSelectIndex: (index) => pickupBySearchResults[index] && selectPickupDriver(pickupBySearchResults[index]),
    onClose: () => setShowPickupByDropdown(false),
    resetKey: pickupBySearchTerm,
  });

  return (
    <div className="space-y-6 pb-32">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Floating action buttons - bottom center */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 flex items-center justify-center gap-3 px-6 py-4 min-w-[min(90vw,720px)] bg-white rounded-2xl shadow-xl border-2 border-gray-200">
        {loading ? (
          <RefreshCw className="w-6 h-6 text-primary animate-spin" />
        ) : (
          <>
            <button
              onClick={() => saveOrder(false)}
              className="px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap"
              title={ui('common.save')}
            >
              <Save className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">{ui('common.save')}</span>
            </button>
            <button
              onClick={() => saveOrder(true)}
              className="px-4 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2 shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap"
              title={ui('common.saveAndPrint')}
            >
              <Save className="w-4 h-4 flex-shrink-0" /> / <Printer className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">{ui('common.saveAndPrint')}</span>
            </button>
            <button
              onClick={() => navigate('/home/orders/order-list')}
              className="px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap"
              title={ui('common.viewOrderList')}
            >
              <Eye className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">{ui('common.view')}</span>
            </button>
            <button
              onClick={initForm}
              className="px-4 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-2 shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap"
              title={ui('common.refresh')}
            >
              <RefreshCw className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">{ui('common.refresh')}</span>
            </button>
            {order.Id > 0 && (
              <>
                <button
                  onClick={() => printOrder()}
                  className="px-4 py-3 bg-sky-600 text-white rounded-xl hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                  title={ui('common.print')}
                >
                  <Printer className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{ui('common.print')}</span>
                </button>
                <button
                  onClick={deleteOrder}
                  className="px-4 py-3 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-300 transition-colors flex items-center gap-2 whitespace-nowrap"
                  title={ui('common.delete')}
                >
                  <Trash2 className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{ui('common.delete')}</span>
                </button>
              </>
            )}
            <div className="w-px h-8 bg-gray-300 mx-1 flex-shrink-0" aria-hidden="true" />
            <button
              onClick={() => getBackOrder('Back')}
              className="px-4 py-3 bg-slate-600 text-white rounded-xl hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap"
              title={ui('common.previous')}
            >
              <ChevronLeft className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">{ui('common.previous')}</span>
            </button>
            <button
              onClick={() => getBackOrder('Next')}
              className="px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap"
              title={ui('common.next')}
            >
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">{ui('common.next')}</span>
            </button>
          </>
        )}
      </div>

      {/* Form Section */}
      <div className="space-y-4">
        {/* Order Basic Information Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{getPageHeading(language, 'createOrderHeading')}</h1>
          <p className="text-sm text-gray-500 mb-3">{getPageHeading(language, 'createOrderSub')}</p>
          {order.Id > 0 && order.Status && (
            <p className="text-sm text-gray-500 mb-4">
              {ui('orders.createOrder.statusPrefix')} {order.Status.Name}
            </p>
          )}
          <div className="border-b border-gray-200 mb-4" />
          <div className="grid grid-cols-12 gap-4">
            {/* Left side - 9 columns */}
            <div className="col-span-9 space-y-4">
              {/* First Row */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('orders.createOrder.voucherNo')}</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={order.VoucherNo}
                    onChange={(e) => setOrder(prev => ({ ...prev, VoucherNo: e.target.value }))}
                    onBlur={checkVoucher}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('orders.createOrder.refNo')}</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={order.StoreRefNo}
                    onChange={(e) => setOrder(prev => ({ ...prev, StoreRefNo: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                    <span>{ui('orders.createOrder.pickUpBy')}</span>
                    {order.PickUpById && (
                      <button
                        onClick={() => {
                          setOrder(prev => ({ ...prev, PickUpById: '' }));
                          setPickupBySearchTerm('');
                        }}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        title={ui('orders.createOrder.clearSelection')}
                        type="button"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                    value={pickupBySearchTerm}
                    onChange={(e) => {
                      setPickupBySearchTerm(e.target.value);
                      if (!e.target.value) {
                        setOrder(prev => ({ ...prev, PickUpById: '' }));
                      }
                    }}
                    onFocus={() => !order.PickUpById && setShowPickupByDropdown(true)}
                    onBlur={() => setTimeout(() => setShowPickupByDropdown(false), 200)}
                    disabled={!!order.PickUpById}
                    placeholder={ui('orders.createOrder.searchSelectDriver')}
                      onKeyDown={pickupNav.onKeyDown}
                  />
                  {pickupDropdownOpen && (
                    <HighlightList
                      items={pickupBySearchResults}
                      highlightedIndex={pickupNav.highlightedIndex}
                      setHighlightedIndex={pickupNav.setHighlightedIndex}
                      setItemRef={pickupNav.setItemRef}
                      onSelect={(d) => selectPickupDriver(d)}
                      renderItem={(d) => <div className="font-medium text-sm">{d.Name}</div>}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {ui('orders.createOrder.payType')}
                    <button
                      type="button"
                      onClick={() => setShowPaymentInfoModal(true)}
                      className="inline-block ml-1 text-blue-500 hover:text-blue-700 cursor-pointer"
                      title={ui('orders.createOrder.paymentTypeDetails')}
                    >
                      <Info className="w-3 h-3" />
                    </button>
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={order.PayType}
                    onChange={(e) => {
                      setOrder(prev => ({ ...prev, PayType: e.target.value }));
                      setTimeout(() => calculateTotals(), 100);
                    }}
                    disabled={companyProfile.HideCODSelection}
                  >
                    <option value="CAD">CAD</option>
                    <option value="COD">COD</option>
                    <option value="CAO">CAO</option>
                    <option value="TAO">TAO</option>
                    <option value="FOC">FOC</option>
                    <option value="APS">APS</option>
                    <option value="APR">APR</option>
                  </select>
                </div>
              </div>

              {/* Second Row */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('orders.createOrder.chargeFrom')}</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={order.ChargeFrom}
                    onChange={(e) => {
                      setOrder(prev => ({ ...prev, ChargeFrom: e.target.value }));
                      checkDeliveryCharges();
                    }}
                  >
                    <option value="CLIENT">{ui('orders.createOrder.chargeFromClient')}</option>
                    <option value="AREA">{ui('orders.createOrder.chargeFromArea')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('orders.createOrder.clientBillNo')}</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={order.ClientBillNo}
                    onChange={(e) => setOrder(prev => ({ ...prev, ClientBillNo: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('orders.createOrder.type')}</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={order.TypeId}
                    onChange={(e) => setOrder(prev => ({ ...prev, TypeId: e.target.value }))}
                  >
                    <option value="">{ui('orders.createOrder.selectType')}</option>
                    {types.map(t => (
                      <option key={t.Id} value={t.Id}>{t.Name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('orders.createOrder.agent')}</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={order.AgentId}
                    onChange={(e) => setOrder(prev => ({ ...prev, AgentId: e.target.value }))}
                  >
                    <option value="">{ui('orders.createOrder.selectAgent')}</option>
                    {agents.map(a => (
                      <option key={a.Id} value={a.Id}>{a.Name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Right side - 3 columns */}
            <div className="col-span-3 flex flex-col justify-center border-l border-gray-200 pl-6">
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    checked={order.IsPickup}
                    onChange={(e) => setOrder(prev => ({ ...prev, IsPickup: e.target.checked }))}
                  />
                  <span className="text-sm font-medium text-gray-700">{ui('orders.createOrder.forPickup')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    checked={order.IsExchange}
                    onChange={(e) => setOrder(prev => ({ ...prev, IsExchange: e.target.checked }))}
                  />
                  <span className="text-sm font-medium text-gray-700">{ui('orders.createOrder.exchange')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    checked={order.IsFragile}
                    onChange={(e) => setOrder(prev => ({ ...prev, IsFragile: e.target.checked }))}
                  />
                  <span className="text-sm font-medium text-gray-700">{ui('orders.createOrder.fragile')}</span>
                </label>
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">{ui('orders.createOrder.deliveringArea')}</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {customerAddress.Location?.Area?.Name || '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Client and Customer Details Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Client Section */}
            <div className="border-r border-gray-200 pr-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{ui('orders.createOrder.sectionClient')}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={refreshClient}
                    className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                    title={ui('orders.createOrder.refreshClient')}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={refreshClientAddress}
                    className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                    title={ui('orders.createOrder.refreshAddress')}
                  >
                    <Home className="w-4 h-4" />
                  </button>
                  <button
                    onClick={searchClientAddress}
                    className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                    title={ui('orders.createOrder.searchAddress')}
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="relative">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.clientName')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={clientSearchTerm}
                      onChange={(e) => {
                        setClientSearchTerm(e.target.value);
                        // Update client object for new clients
                        if (!order.ClientId) {
                          setClient(prev => ({ ...prev, Name: e.target.value }));
                        }
                      }}
                      disabled={order.ClientId > 0}
                      placeholder={ui('orders.createOrder.phSearchNewClient')}
                      onKeyDown={clientNameNav.onKeyDown}
                    />
                    {clientDropdownOpen && (
                      <HighlightList
                        items={clientSearchResults}
                        highlightedIndex={clientNameNav.highlightedIndex}
                        setHighlightedIndex={clientNameNav.setHighlightedIndex}
                        setItemRef={clientNameNav.setItemRef}
                        onSelect={(c) => selectClient(c)}
                        renderItem={(c) => (
                          <>
                            <div className="font-medium text-sm">{c.Name}</div>
                            <div className="text-xs text-gray-500">{c.ClientNo} - {c.MobileNo1}</div>
                          </>
                        )}
                      />
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.clientNo')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={clientNoSearchTerm}
                      onChange={(e) => {
                        setClientNoSearchTerm(e.target.value);
                        // Update client object for new clients
                        if (!order.ClientId) {
                          setClient(prev => ({ ...prev, ClientNo: e.target.value }));
                        }
                      }}
                      disabled={order.ClientId > 0}
                      placeholder={ui('orders.createOrder.phTypeClientNumber')}
                      onKeyDown={clientNoNav.onKeyDown}
                    />
                    {clientNoDropdownOpen && (
                      <HighlightList
                        items={clientSearchResults}
                        highlightedIndex={clientNoNav.highlightedIndex}
                        setHighlightedIndex={clientNoNav.setHighlightedIndex}
                        setItemRef={clientNoNav.setItemRef}
                        onSelect={(c) => selectClient(c)}
                        renderItem={(c) => (
                          <>
                            <div className="font-medium text-sm">{c.Name}</div>
                            <div className="text-xs text-gray-500">{c.ClientNo} - {c.MobileNo1}</div>
                          </>
                        )}
                      />
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.mobileNo')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={clientMobileSearchTerm}
                      onChange={(e) => {
                        setClientMobileSearchTerm(e.target.value);
                        // Update client object for new clients
                        if (!order.ClientId) {
                          setClient(prev => ({ ...prev, MobileNo1: e.target.value }));
                        }
                      }}
                      disabled={order.ClientId > 0}
                      placeholder={ui('orders.createOrder.phTypeMobile')}
                      onKeyDown={clientMobileNav.onKeyDown}
                    />
                    {clientMobileDropdownOpen && (
                      <HighlightList
                        items={clientSearchResults}
                        highlightedIndex={clientMobileNav.highlightedIndex}
                        setHighlightedIndex={clientMobileNav.setHighlightedIndex}
                        setItemRef={clientMobileNav.setItemRef}
                        onSelect={(c) => selectClient(c)}
                        renderItem={(c) => (
                          <>
                            <div className="font-medium text-sm">{c.Name}</div>
                            <div className="text-xs text-gray-500">{c.ClientNo} - {c.MobileNo1}</div>
                          </>
                        )}
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.emirate')}</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={clientAddress.EmirateId}
                      onChange={(e) => setClientAddress(prev => ({ ...prev, EmirateId: e.target.value }))}
                      disabled={order.ClientAddressId > 0}
                    >
                      <option value="">{ui('orders.createOrder.selectEmirate')}</option>
                      {emirates.map(e => (
                        <option key={e.Id} value={e.Id}>{e.Name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="relative">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.location')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={clientLocationSearchTerm}
                      onChange={(e) => setClientLocationSearchTerm(e.target.value)}
                      onFocus={() => !order.ClientAddressId && clientAddress.EmirateId && setShowClientLocationDropdown(true)}
                      onBlur={() => setTimeout(() => setShowClientLocationDropdown(false), 200)}
                      disabled={order.ClientAddressId > 0 || !clientAddress.EmirateId}
                      placeholder={ui('orders.createOrder.phSearchLocation')}
                      onKeyDown={clientLocationNav.onKeyDown}
                    />
                    {clientLocationOpen && (
                      <HighlightList
                        items={clientLocationResults}
                        highlightedIndex={clientLocationNav.highlightedIndex}
                        setHighlightedIndex={clientLocationNav.setHighlightedIndex}
                        setItemRef={clientLocationNav.setItemRef}
                        onSelect={(loc) => selectClientLocation(loc)}
                        renderItem={(loc) => <div className="text-sm">{loc.Name}</div>}
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.locationUrl')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={clientAddress.LocationUrl}
                      onChange={(e) => setClientAddress(prev => ({ ...prev, LocationUrl: e.target.value }))}
                      disabled={order.ClientAddressId > 0}
                      placeholder={ui('orders.createOrder.phUrl')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-9">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.address')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={clientAddress.Address}
                      onChange={(e) => setClientAddress(prev => ({ ...prev, Address: e.target.value }))}
                      disabled={order.ClientAddressId > 0}
                      placeholder={ui('orders.createOrder.phEnterAddress')}
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.poBox')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={clientAddress.POBOX}
                      onChange={(e) => setClientAddress(prev => ({ ...prev, POBOX: e.target.value }))}
                      disabled={order.ClientAddressId > 0}
                      placeholder={ui('orders.createOrder.phPoBox')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Section */}
            <div className="pl-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{ui('orders.createOrder.sectionCustomer')}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={refreshCustomer}
                    className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                    title={ui('orders.createOrder.refreshCustomer')}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setOrder(prev => ({ ...prev, ISCustomerEdit: true }))}
                    className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                    title={ui('orders.createOrder.editCustomer')}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={refreshCustomerAddress}
                    className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                    title={ui('orders.createOrder.refreshAddress')}
                  >
                    <Home className="w-4 h-4" />
                  </button>
                  <button
                    onClick={searchCustomerAddress}
                    className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                    title={ui('orders.createOrder.searchAddress')}
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="relative">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.customerName')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={customerSearchTerm}
                      onChange={(e) => {
                        setCustomerSearchTerm(e.target.value);
                        // Update customer object for new customers
                        if (!order.CustomerId) {
                          setCustomer(prev => ({ ...prev, Name: e.target.value }));
                        }
                      }}
                      disabled={order.CustomerId > 0 && !order.ISCustomerEdit}
                      placeholder={ui('orders.createOrder.phSearchNewCustomer')}
                      onKeyDown={customerNameNav.onKeyDown}
                    />
                    {customerDropdownOpen && (
                      <HighlightList
                        items={customerSearchResults}
                        highlightedIndex={customerNameNav.highlightedIndex}
                        setHighlightedIndex={customerNameNav.setHighlightedIndex}
                        setItemRef={customerNameNav.setItemRef}
                        onSelect={(c) => selectCustomer(c)}
                        renderItem={(c) => (
                          <>
                            <div className="font-medium text-sm">{c.Name}</div>
                            <div className="text-xs text-gray-500">{c.MobileNo1}</div>
                          </>
                        )}
                      />
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.mobileNo1')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={customerMobileSearchTerm}
                      onChange={(e) => {
                        setCustomerMobileSearchTerm(e.target.value);
                        // Update customer object for new customers
                        if (!order.CustomerId) {
                          setCustomer(prev => ({ ...prev, MobileNo1: e.target.value }));
                        }
                      }}
                      disabled={order.CustomerId > 0 && !order.ISCustomerEdit}
                      placeholder={ui('orders.createOrder.phTypeMobile')}
                      onKeyDown={customerMobileNav.onKeyDown}
                    />
                    {customerMobileDropdownOpen && (
                      <HighlightList
                        items={customerSearchResults}
                        highlightedIndex={customerMobileNav.highlightedIndex}
                        setHighlightedIndex={customerMobileNav.setHighlightedIndex}
                        setItemRef={customerMobileNav.setItemRef}
                        onSelect={(c) => selectCustomer(c)}
                        renderItem={(c) => (
                          <>
                            <div className="font-medium text-sm">{c.Name}</div>
                            <div className="text-xs text-gray-500">{c.MobileNo1}</div>
                          </>
                        )}
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.mobileNo2')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={customer.MobileNo2}
                      onChange={(e) => setCustomer(prev => ({ ...prev, MobileNo2: e.target.value }))}
                      disabled={order.CustomerId > 0 && !order.ISCustomerEdit}
                      placeholder={ui('orders.createOrder.phOptional')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.emirate')}</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={customerAddress.EmirateId}
                      onChange={(e) => setCustomerAddress(prev => ({ ...prev, EmirateId: e.target.value }))}
                      disabled={order.CustomerAddressId > 0 && !order.ISCustomerEdit}
                    >
                      <option value="">{ui('orders.createOrder.selectEmirate')}</option>
                      {emirates.map(e => (
                        <option key={e.Id} value={e.Id}>{e.Name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="relative">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.location')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={customerLocationSearchTerm}
                      onChange={(e) => setCustomerLocationSearchTerm(e.target.value)}
                      onFocus={() => (!order.CustomerAddressId || order.ISCustomerEdit) && customerAddress.EmirateId && setShowCustomerLocationDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCustomerLocationDropdown(false), 200)}
                      disabled={(order.CustomerAddressId > 0 || !customerAddress.EmirateId) && !order.ISCustomerEdit}
                      placeholder={ui('orders.createOrder.phSearchLocation')}
                      onKeyDown={customerLocationNav.onKeyDown}
                    />
                    {customerLocationOpen && (
                      <HighlightList
                        items={customerLocationResults}
                        highlightedIndex={customerLocationNav.highlightedIndex}
                        setHighlightedIndex={customerLocationNav.setHighlightedIndex}
                        setItemRef={customerLocationNav.setItemRef}
                        onSelect={(loc) => selectCustomerLocation(loc)}
                        renderItem={(loc) => <div className="text-sm">{loc.Name}</div>}
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.locationUrl')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={customerAddress.LocationUrl}
                      onChange={(e) => setCustomerAddress(prev => ({ ...prev, LocationUrl: e.target.value }))}
                      disabled={order.CustomerAddressId > 0 && !order.ISCustomerEdit}
                      placeholder={ui('orders.createOrder.phUrl')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-9">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.address')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={customerAddress.Address}
                      onChange={(e) => setCustomerAddress(prev => ({ ...prev, Address: e.target.value }))}
                      disabled={order.CustomerAddressId > 0 && !order.ISCustomerEdit}
                      placeholder={ui('orders.createOrder.phEnterAddress')}
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.poBox')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                      value={customerAddress.POBOX}
                      onChange={(e) => setCustomerAddress(prev => ({ ...prev, POBOX: e.target.value }))}
                      disabled={order.CustomerAddressId > 0 && !order.ISCustomerEdit}
                      placeholder={ui('orders.createOrder.phPoBox')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Details and Financial Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{ui('orders.createOrder.orderDetailsFinancial')}</h2>
          
          {/* Remarks and Dates */}
          <div className="grid grid-cols-12 gap-3 mb-4">
            <div className="col-span-6">
              <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.remarks')}</label>
              <textarea
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                value={order.Remarks}
                onChange={(e) => setOrder(prev => ({ ...prev, Remarks: e.target.value }))}
                placeholder={ui('orders.createOrder.phRemarks')}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.quantity')}</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                value={order.Quantity}
                onChange={(e) => setOrder(prev => ({ ...prev, Quantity: parseInt(e.target.value) || 1 }))}
                min="1"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.pickupDate')}</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                value={order.PickupDate}
                onChange={(e) => setOrder(prev => ({ ...prev, PickupDate: e.target.value }))}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.deliveryDate')}</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                value={order.DeliveryDate}
                onChange={(e) => setOrder(prev => ({ ...prev, DeliveryDate: e.target.value }))}
              />
            </div>
          </div>

          {/* Financial Details */}
          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-5 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.productPrice')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                  value={order.ProductPrice}
                  onChange={(e) => {
                    setOrder(prev => ({ ...prev, ProductPrice: parseFloat(e.target.value) || 0 }));
                    setTimeout(() => calculateTotals(), 500);
                  }}
                  disabled={companyProfile.IsHidePaymentEditOnOrder && order.Id > 0}
                  placeholder={ui('orders.createOrder.phAmount')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.deliveryCharge')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                  value={order.DeliveryCharge}
                  onChange={(e) => {
                    setOrder(prev => ({
                      ...prev,
                      DeliveryCharge: parseFloat(e.target.value) || 0,
                      TempServiceCharge: parseFloat(e.target.value) || 0,
                      delchargechanged: true,
                    }));
                    setTimeout(() => calculateTotals(), 500);
                  }}
                  disabled={companyProfile.IsHideDeliveryEditOnOrder && order.Id > 0}
                  placeholder={ui('orders.createOrder.phAmount')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {ui('orders.createOrder.tax')}
                  {companyProfile.DefTAX ? ` (${companyProfile.DefTAX}%)` : ''}
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-700 font-medium"
                  value={order.TAX}
                  disabled
                  readOnly
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.clientAmount')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-blue-50 text-sm text-blue-900 font-semibold"
                  value={order.ClientAmount}
                  disabled
                  readOnly
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{ui('orders.createOrder.customerAmount')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-green-50 text-sm text-green-900 font-semibold"
                  value={order.CustomerAmount}
                  disabled
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 relative z-[100000]">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{ui('orders.createOrder.confirmDeleteTitle')}</h3>
            <p className="text-gray-600 mb-6">{ui('orders.createOrder.confirmDeleteBody')}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {ui('common.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {ui('common.confirm')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Client Address Modal */}
      {showClientAddressModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto relative z-[100000]">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{ui('orders.createOrder.clientAddressTitle')}</h3>
            </div>
            <div className="p-6">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-2">{ui('columns.emirate')}</th>
                    <th className="text-left p-2">{ui('columns.location')}</th>
                    <th className="text-left p-2">{ui('columns.area')}</th>
                    <th className="text-left p-2">{ui('columns.address')}</th>
                    <th className="text-left p-2">{ui('orders.createOrder.addressTablePoBox')}</th>
                    <th className="text-left p-2">{ui('columns.option')}</th>
                  </tr>
                </thead>
                <tbody>
                  {clientAddresses.map(addr => (
                    <tr key={addr.Id} className="border-b border-gray-100">
                      <td className="p-2">{addr.Emirate?.Name}</td>
                      <td className="p-2">{addr.Location?.Name}</td>
                      <td className="p-2">{addr.Location?.AreaName}</td>
                      <td className="p-2">{addr.Address}</td>
                      <td className="p-2">{addr.POBOX}</td>
                      <td className="p-2">
                        <button
                          onClick={() => selectClientAddressFromModal(addr)}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          {ui('common.select')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowClientAddressModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {ui('common.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Customer Address Modal */}
      {showCustomerAddressModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto relative z-[100000]">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{ui('orders.createOrder.customerAddressTitle')}</h3>
            </div>
            <div className="p-6">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-2">{ui('columns.emirate')}</th>
                    <th className="text-left p-2">{ui('columns.location')}</th>
                    <th className="text-left p-2">{ui('columns.area')}</th>
                    <th className="text-left p-2">{ui('columns.address')}</th>
                    <th className="text-left p-2">{ui('orders.createOrder.addressTablePoBox')}</th>
                    <th className="text-left p-2">{ui('columns.options')}</th>
                  </tr>
                </thead>
                <tbody>
                  {customerAddresses.map(addr => (
                    <tr key={addr.Id} className="border-b border-gray-100">
                      <td className="p-2">{addr.Emirate?.Name}</td>
                      <td className="p-2">{addr.Location?.Name}</td>
                      <td className="p-2">{addr.Location?.AreaName}</td>
                      <td className="p-2">{addr.Address}</td>
                      <td className="p-2">{addr.POBOX}</td>
                      <td className="p-2">
                        <button
                          onClick={() => selectCustomerAddressFromModal(addr)}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          {ui('common.select')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowCustomerAddressModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {ui('common.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Payment Type Info Modal */}
      {showPaymentInfoModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 relative z-[100000]">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{ui('orders.createOrder.paymentTypeDetails')}</h3>
            </div>
            <div className="p-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left p-3 font-semibold text-gray-900">{ui('orders.createOrder.paymentTableType')}</th>
                    <th className="text-left p-3 font-semibold text-gray-900">{ui('orders.createOrder.paymentTableRemarks')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-3 font-medium">CAD</td>
                    <td className="p-3 text-gray-700">{ui('orders.createOrder.payCad')}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-3 font-medium">COD</td>
                    <td className="p-3 text-gray-700">{ui('orders.createOrder.payCod')}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-3 font-medium">CAO</td>
                    <td className="p-3 text-gray-700">{ui('orders.createOrder.payCao')}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-3 font-medium">TAO</td>
                    <td className="p-3 text-gray-700">{ui('orders.createOrder.payTao')}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-3 font-medium">FOC</td>
                    <td className="p-3 text-gray-700">{ui('orders.createOrder.payFoc')}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-3 font-medium">APS</td>
                    <td className="p-3 text-gray-700">{ui('orders.createOrder.payAps')}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="p-3 font-medium">APR</td>
                    <td className="p-3 text-gray-700">{ui('orders.createOrder.payApr')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowPaymentInfoModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {ui('common.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CreateOrder;
